import { Attachment, ItemsPerPage } from '@billy/design-system'
import { useCallback, useMemo } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router'

import { AttachmentFillType } from '@features/attachments/enums/attachmentFillType'
import { AttachmentsSortProperty } from '@features/attachments/enums/attachmentsSortProperty'
import { useFetchAttachments } from '@features/attachments/hooks/useFetchAttachments'
import { getAttachmentsApiFilter } from '@features/attachments/utils/getAttachmentsApiFilter'
import { useIsEInvoicingOnly } from '@features/bills/hooks/useIsEInvoicingOnly'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { useBulkNavigationQueryParams } from '../../../hooks/useBulkNavigationQueryParams'
import { Paging } from '../../../types/metable'
import { getOrganizationViewUrl } from '../../../utils/routing/getOrganizationViewUrl'
import { reactRoute } from '../../../utils/routing/reactRoute'
import { useBulkAttachmentsContext } from '../contexts/bulkAttachmentsContext'
import { BillUrlParams } from '../types/billUrlParams'
import { useBulkNavigation } from './useBulkNavigation'

export const useBulkAttachmentsNavigation = () => {
  const { attachmentIdBulk } = useParams<BillUrlParams>()
  const isEnabled = !!attachmentIdBulk
  const [bulkQueryParams, setBulkQueryParams] = useBulkNavigationQueryParams({
    enabled: isEnabled,
  })
  const { attachmentIdsToEdit: selectedAttachmentIds } = useBulkAttachmentsContext()
  const navigate = useNavigate()
  const location = useLocation()
  const { organization } = useUserOrganization()
  const isEInvoicingOnly = useIsEInvoicingOnly()

  const { bulkPage, bulkPageSize, bulkSortDirection, bulkSortProperty, bulkUploadType } = bulkQueryParams
  const hasAllPropertiesDefined = bulkPage && bulkPageSize && bulkSortDirection && bulkSortProperty && bulkUploadType
  const page = Number(bulkPage)
  const pageSize = Number(bulkPageSize) as ItemsPerPage
  const { type: apiType, eInvoiceFilter } = getAttachmentsApiFilter(bulkUploadType, false, isEInvoicingOnly)

  const handleChangePage = useCallback(
    (attachmentIdBulk: string | undefined) => {
      navigate(
        `${getOrganizationViewUrl(
          organization?.url,
          reactRoute.organization.bills.getEditBulkAttachmentsRoute(attachmentIdBulk || ''),
        )}${location.search}`,
      )
    },
    [navigate, location.search, organization?.url],
  )

  const { attachments, isLoading, pagination } = useFetchAttachments({
    isDisabled: !isEnabled || !hasAllPropertiesDefined,
    page,
    pageSize,
    sortDirection: bulkSortDirection,
    sortBy: bulkSortProperty as AttachmentsSortProperty,
    searchQuery: '',
    fillType: AttachmentFillType.Nonfilled,
    type: apiType,
    eInvoiceFilter,
  })

  const attachmentsFiltered = useMemo(() => {
    if (!selectedAttachmentIds.length) {
      return attachments
    }

    const attachmentMap = new Map(attachments.map((attachment) => [attachment.id, attachment]))
    return selectedAttachmentIds.map((id) => attachmentMap.get(id)).filter(Boolean) as Attachment[]
  }, [attachments, selectedAttachmentIds])

  const paginationFiltered: Paging = {
    pageCount: pagination?.pageCount || 0,
    total: attachmentsFiltered?.length || pagination?.total || 0,
    pageSize: pagination?.pageSize || 0,
    page: pagination?.page || 0,
  }

  const { itemsToGo, next, previous, hasNext, hasPrevious } = useBulkNavigation({
    currentId: attachmentIdBulk,
    isLoading,
    items: attachmentsFiltered,
    page,
    onPageChange: handleChangePage,
    onQueryParamsChange: setBulkQueryParams,
    pageSize,
    pagination: paginationFiltered,
  })

  return {
    attachmentsToGo: itemsToGo,
    goToNextAttachment: next,
    goToPreviousAttachment: previous,
    hasNextAttachment: hasNext,
    hasPreviousAttachment: hasPrevious,
    isLoading,
  }
}
